/**
 * The words and limits of a chain of title, shared by every screen that writes
 * or reads one — the register wizard, the work record and the public room.
 *
 * The eight entry kinds and their per-kind field labels are large enough to
 * live in `historyKinds.constants.ts`, re-exported from here so importers see
 * one vocabulary. What remains is what is the same on every kind: the evidence
 * grades, the confirmation strip, the document rules and the composers.
 *
 * Two of the caps are mirrors of database constraints, not taste —
 * `ACCEPTED_DOCUMENT_TYPES` matches the check on `ondo.provenance_documents`
 * — and everything that reads like a verdict is written as a count, because the
 * registry shows who stands behind a link and never decides for the reader.
 */

import {
  MAX_PROVENANCE_DOCUMENT_BYTES,
  PROVENANCE_DOCUMENT_CONTENT_TYPES,
} from "@ondo/shared";
import type {
  ProvenanceDocumentKind,
  ProvenanceEvidenceClaim,
  ProvenanceMethod,
  ProvenanceStanding,
} from "@ondo/shared";

export * from "./historyKinds.constants.ts";

/**
 * The evidence pill, and the one mapping that decides both its words and its
 * colour.
 *
 * `pill` is uppercase in the string rather than in CSS so a screen reader hears
 * it once and a copy-paste carries it. `token` names a custom property rather
 * than a hex, exactly as `accentFor` returns `var(--role-collector)` — one
 * mapping, here, next to the labels, so a fifth grade cannot arrive with a
 * colour and no name or the reverse.
 *
 * READ THE OPTION LABELS. Each one says whose statement it is, in the option
 * itself, because the pill alone reads like a finding. "Verified fact" is what
 * the person filling in the form asserts about their own paperwork; what the
 * REGISTRY has to say sits beside it as a count of who put their name behind it.
 */
export const EVIDENCE_GRADES: readonly {
  key: ProvenanceEvidenceClaim;
  pill: string;
  label: string;
  token: string;
  bgToken: string;
}[] = [
  {
    key: "documented",
    pill: "VERIFIED FACT",
    label: "Verified fact — I hold primary documentation for this",
    token: "var(--grade-documented)",
    bgToken: "var(--grade-documented-bg)",
  },
  {
    key: "secondary_literature",
    pill: "SECONDARY LITERATURE",
    label: "Secondary literature — published or catalogued, not held by me",
    token: "var(--grade-secondary)",
    bgToken: "var(--grade-secondary-bg)",
  },
  {
    key: "owner_claim",
    pill: "USER SUBMITTED CLAIM",
    label: "My own claim — from memory, family or trade knowledge",
    token: "var(--grade-claim)",
    bgToken: "var(--grade-claim-bg)",
  },
  {
    key: "unverified",
    pill: "UNVERIFIED PROVENANCE",
    label: "Unverified — recorded for completeness, no evidence held",
    token: "var(--grade-unverified)",
    bgToken: "var(--grade-unverified-bg)",
  },
];

/**
 * What a new entry starts as.
 *
 * The weakest value, deliberately. A default of anything else would put words in
 * the submitter's mouth on the one field that is entirely their assertion.
 */
export const DEFAULT_GRADE: ProvenanceEvidenceClaim = "unverified";

export const ACQUISITION_METHODS: readonly { key: ProvenanceMethod; label: string }[] = [
  { key: "purchase", label: "Purchase" },
  { key: "created_by_artist", label: "Created by artist" },
  { key: "commission", label: "Commission" },
  { key: "gift", label: "Gift" },
  { key: "bequest", label: "Bequest" },
  { key: "inheritance", label: "Inheritance" },
  { key: "exchange", label: "Exchange" },
  { key: "auction", label: "Auction" },
  { key: "consignment", label: "Consignment" },
  { key: "restitution", label: "Restitution" },
  { key: "unknown", label: "Not known" },
];

export const DEFAULT_METHOD: ProvenanceMethod = "purchase";

/**
 * The confirmation strip: the one line that says who stands behind this link.
 *
 * `CONFIRMED` is a count wearing a word, never a finding — which is why the
 * sentence beside it always says how many, and why a single dispute takes the
 * whole strip out of green whatever the confirmations say. That is the same
 * rule `workBadge` applies to a work, and for the same reason: the registry
 * does not resolve a disagreement, it shows that there is one.
 */
export const CONFIRMATION_COPY = {
  confirmed: "✓ CONFIRMED",
  disputed: "⚠ DISPUTED",
  unconfirmed: "UNCONFIRMED",
  draft: "DRAFT",

  /** Appended to the count, and the reason the chevron is worth pressing. */
  openHint: "open to see who and when",
  unconfirmedNote: "nobody has confirmed this entry yet",
  draftNote: "Not on the record yet — open it to edit, then save it.",
  draftVerified:
    "Not on the record yet — your verification is filed with it the moment you save.",

  confirmationsTitle: "Confirmations",
  commentsTitle: "Comments",
  loading: "Loading confirmations…",
  failed: "The confirmations could not be loaded. Nothing has changed on the record.",
  none: "Nothing has been recorded against this entry — no confirmation, no dispute, no comment.",

  /** Beside a name, so 'mine' is legible without a colour. */
  you: "you",
  /**
   * A position taken and then taken back. The row stays because the position
   * was held, and hiding it would make the counts above it look wrong.
   */
  withdrawn: "withdrawn — no longer counted",

  /**
   * The control on the reader's own live position.
   *
   * "Take back" and not "Delete", because nothing is deleted: `ended_at` is
   * set, the row stays on the list marked withdrawn, and the count above drops
   * by one. A label promising removal would describe an audit log that its own
   * subject can erase, which this deliberately is not.
   */
  takeBack: "Take back",

  /**
   * A comment taken back — OND-21. Not `withdrawn` above, whose "no longer
   * counted" is about a position: a comment never counted, so the mark says
   * only what happened. The row stays, because a reply to a comment that had
   * vanished would be answering nothing.
   */
  commentWithdrawn: "withdrawn",
  /**
   * The control on the reader's own live comment. "Withdraw" and not "Delete",
   * for `takeBack`'s reason: nothing is deleted, the row stays marked.
   */
  withdrawComment: "Withdraw comment",

  /**
   * WHO confirmed, not WHAT they are.
   *
   * The design puts a role chip here — ESTATE, EXPERT — and the registry does
   * not record one: `provenance_attestations_for` returns a name and an ONDO id
   * and nothing else, because an attestor consented to being named rather than
   * to being classified. A chip guessed from a display name would be the client
   * inventing an authority.
   */
  roleUnknown: "Role not recorded",
} as const;

export const DOCUMENT_KINDS: readonly { key: ProvenanceDocumentKind; label: string }[] = [
  { key: "invoice", label: "Invoice" },
  { key: "receipt", label: "Receipt" },
  { key: "certificate", label: "Certificate" },
  { key: "photograph", label: "Photograph" },
  { key: "correspondence", label: "Correspondence" },
  { key: "exhibition_catalogue", label: "Exhibition catalogue" },
  { key: "archival_record", label: "Archival record" },
  { key: "shipping_record", label: "Shipping record" },
  // OND-62. Both were named on camera and both used to arrive as something
  // else: an import certificate as a shipping record or as Other, and a
  // provenance declaration as Other. They sit ABOVE Other because the list
  // reads as real categories ending in a catch-all, which is the order the
  // enum has too.
  { key: "import_certificate", label: "Import certificate" },
  { key: "provenance_declaration", label: "Provenance declaration" },
  // OND-95, and above Other for the same reason: a schedule is not a
  // certificate, and filing it under Other loses the fact that it is the cover.
  { key: "policy", label: "Insurance policy" },
  { key: "other", label: "Other" },
];

export const DEFAULT_DOCUMENT_KIND: ProvenanceDocumentKind = "other";

/**
 * The currency a period of insurance opens on.
 *
 * The same answer `sellWizard.constants.ts` gives, for the same reason — most
 * of these records are London — and an editable field rather than a setting,
 * because a policy written in dollars is one keystroke away.
 */
export const DEFAULT_INSURED_CURRENCY = "GBP";

/**
 * What the document picker accepts.
 *
 * PDF is the whole reason this list exists apart from ACCEPTED_IMAGE_TYPES: the
 * check constraint on ondo.work_images allows five image types and nothing else,
 * which is why a document goes to a table and a bucket of its own.
 * The list matches the constraint on ondo.provenance_documents, and that is not
 * a coincidence to be tidied away — a file this lets through and the constraint
 * refuses would fail at the end of a submission.
 *
 * **So it is no longer written out here.** It is `@ondo/shared`'s one list,
 * which `bucket-contract.test.ts` holds to `[storage.buckets.provenance]` in
 * `supabase/config.toml` — the only copy of this fact anything can check. The
 * name stays, because the picker and the draft both read it.
 */
export const ACCEPTED_DOCUMENT_TYPES = PROVENANCE_DOCUMENT_CONTENT_TYPES;

/**
 * 25MB, as the bucket says — and now from the one place that says it.
 *
 * The phone had the same figure from the WORK bucket's 50MB and refused
 * nothing under it (OND-147), so the three copies became one.
 */
export const MAX_DOCUMENT_BYTES = MAX_PROVENANCE_DOCUMENT_BYTES;

export const MAX_DOCUMENTS_PER_ENTRY = 12;

/**
 * How many comments, and how many people to ask, one entry may queue.
 *
 * Every other list in this model has a cap. A queued-ask list without one is a
 * way to notify forty accounts from a form, and each ask discloses which work
 * and whose claim.
 */
export const MAX_ACTS_PER_ENTRY = 12;

/** Longer than any chain anyone has typed by hand, and a cap on a paste. */
export const MAX_ENTRIES = 40;

/**
 * Full names, not abbreviations, because these are what gets stored.
 *
 * The month select writes its label straight into the entry text — 'June 1938'
 * is the record — so a shortened list here would shorten the archive.
 */
export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * How far back the year select goes.
 *
 * Far enough for a nineteenth-century chain, and no further: every year added
 * below this is one more row somebody scrolls past on every entry they write.
 */
export const EARLIEST_PROVENANCE_YEAR = 1800;

/**
 * What is left of this after the eight kinds took the rest.
 *
 * Owner, gallery, location, method and the two date labels used to live here as
 * one fixed set, because there was one shape of entry. They are now per-kind and
 * live in `HISTORY_KINDS` — "Owner / Collector" is what a sale calls its first
 * field and "Venue / museum" is what an exhibition calls its second, and a
 * single list could only have named one of them.
 *
 * These four survive because they are the same on every kind.
 */
export const PROVENANCE_LABELS = {
  grade: "Evidence status",
  documents: "Supporting documents",
  documentKind: "What this document is",
  verification: "Verification",
  timeline: "Provenance timeline",
} as const;

export const VERIFICATION_LABELS = {
  verify: "✓ Verify",
  dispute: "✕ Dispute",
  comment: "💬 Comment",
  request: "↗ Request Verification",
} as const;

/**
 * Accepting or rejecting ONE paper — OND-62.
 *
 * SEPARATE FROM `VERIFICATION_LABELS` ON PURPOSE, and the words are different
 * on purpose too. Verify and Dispute are what you say about a CLAIM; Accept and
 * Reject are what an estate does to a paper in a dossier, which is the
 * walkthrough's own wording: "accept them or reject them". One screen shows
 * both sets at once, and two controls reading "✕ Dispute" — one about the sale,
 * one about its invoice — is the conflation this ticket exists to undo,
 * arriving in a button.
 *
 * The stance behind each is the same `AttestationStance` the entry uses, which
 * is the reuse that matters. These are only the words.
 */
export const DOCUMENT_VERDICT_LABELS = {
  verify: "✓ Accept",
  dispute: "✕ Reject",
  /** On the reader's own live verdict, which is the only one they may end. */
  withdraw: "Undo",
} as const;

/**
 * What a rejection has to say, and why the field is not optional.
 *
 * `VERIFICATION_COMPOSERS.dispute` one level down, with the subject changed
 * from the claim to the paper. The database refuses an empty one —
 * `provenance_document_verdicts_dispute_reasoned` — so this is where the reader
 * is told that before the server says it.
 */
export const DOCUMENT_REJECTION_COMPOSER: ComposerModel = {
  label: "Why you reject this document",
  placeholder: "e.g. The studio issued no certificate for this work.",
  help: "Required. Calling somebody's paperwork a forgery with no reason is an accusation they cannot answer, and the database refuses it anyway.",
  action: "Reject document",
  multiline: true,
};

/**
 * How a contested entry reads, and the one word this mapping will not say.
 *
 * `EVIDENCE_GRADES`'s shape — a pill, a sentence and a token — for the same
 * reason: one mapping, beside the labels, so a state cannot arrive with a
 * colour and no words.
 *
 * THERE IS NO `confirmed` ENTRY HERE BECAUSE THERE IS NO SUCH STANDING. The
 * registry counts who stands behind a claim and concludes nothing, so the
 * strongest thing this says about an unchallenged entry is that nothing stands
 * against it. `uncontested` therefore gets the quietest treatment of the three
 * — it is the ordinary case and must not read as a badge.
 */
export const STANDING_PILLS: Record<
  ProvenanceStanding,
  { pill: string; sentence: string; token: string; bgToken: string }
> = {
  uncontested: {
    pill: "",
    sentence: "Nothing on the record stands against this entry.",
    token: "var(--grade-unverified)",
    bgToken: "var(--grade-unverified-bg)",
  },
  questionable: {
    pill: "QUESTIONABLE",
    sentence:
      "A document on this entry has been rejected, so the claim it supports is in question. Nobody has disputed the entry itself.",
    token: "var(--grade-claim)",
    bgToken: "var(--grade-claim-bg)",
  },
  disputed: {
    pill: "DISPUTED",
    sentence: "Somebody has put their name against this entry itself.",
    token: "var(--grade-unverified)",
    bgToken: "var(--grade-unverified-bg)",
  },
};

/**
 * What an act needs typed before it can be recorded.
 *
 * Here rather than in the component for the reason every other label is: a
 * composer is a field with a label and a help line, and none of those is a
 * decision the view gets to make. It also replaces three `window.prompt` calls,
 * which could not be styled, could not show the model's refusal beside the
 * field, and are blocked outright in some browsers.
 */
export interface ComposerModel {
  label: string;
  placeholder: string;
  help: string;
  /** The button that records it. */
  action: string;
  /** A textarea rather than an input. */
  multiline: boolean;
}

export const VERIFICATION_COMPOSERS: Record<"dispute" | "comment" | "request", ComposerModel> = {
  dispute: {
    label: "Why you dispute this entry",
    placeholder: "What is wrong with it, and how you know.",
    help: "Required. An accusation with no content is one the other side cannot answer, and the database refuses it anyway.",
    action: "Record dispute",
    multiline: true,
  },
  comment: {
    label: "What you want to add",
    placeholder: "e.g. The Beyeler archive dates this to 1961.",
    help: "A comment is context, not a position. Anyone who can read this entry can leave one, and it changes no count.",
    action: "Record comment",
    multiline: true,
  },
  request: {
    label: "ONDO id or email address of the party to ask",
    placeholder: "ONDO-1042, or name@example.com",
    // Both halves of this matter, and the second is the newer one.
    //
    // NO NAME SEARCH, still: ONDO will not tell one account whether another
    // exists, so both spellings are typed from something the asker already had.
    //
    // AND AN ADDRESS ALWAYS WORKS, which is not a convenience — it is the same
    // rule seen from the other side. If an address that was already on ONDO
    // behaved differently from one that was not, the form would answer the
    // question the first half refuses. So it never reports either, and the
    // party finds a request waiting when they sign in.
    help: "There is no name search: ONDO will not tell one account whether another exists, so you can only name somebody you already have an id or an address for. An address works whether or not they are on ONDO — they will be asked to sign in and answer.",
    action: "Add to the ask list",
    multiline: false,
  },
};

export const PROVENANCE_COPY = {
  addEntry: "+ Add Entry",
  upload: "+ Upload",
  removeEntry: "Remove entry",
  withdrawEntry: "Withdraw entry",


  /** The empty option in each of the three date selects. */
  dateBlank: "—",
  dateDay: "Day",
  dateMonth: "Month",
  dateYear: "Year",

  tooManyEntries: `An artwork can carry ${String(MAX_ENTRIES)} history entries.`,

  /**
   * Said wherever the grade select appears, because the pill on its own reads
   * like a finding and this is the one field that is purely an assertion.
   */
  gradeHelp:
    "This is your description of your own evidence. ONDO does not check it — the pill says who made the claim, not what the registry found. The count beside it is who has put their name behind this entry.",
  /** Visually hidden, so the pill is announced as what it is. */
  gradeQualifier: "Submitter’s own evidence grade:",

  documentsEmpty:
    "No documents attached. Upload invoices, receipts, photographs, or archival records.",
  documentsHelp: "PDF, JPEG, PNG, TIFF — up to 25MB each.",

  // ── OND-62. What the record holds about one paper ──
  /** Prefixes a count: 'Accepted by 2'. */
  acceptedBy: "Accepted by",
  rejectedBy: "Rejected by",
  /** The pair `plural` picks from, so the noun stays copy and the arithmetic does not. */
  rejectedDocuments: ["document", "documents"] as const,
  /** Completes the note: '2 documents rejected on this entry'. */
  rejectedSuffix: "rejected on this entry",
  /**
   * Said beside the two controls, because the per-paper verdict is the thing a
   * reader is most likely to take for the per-entry one.
   */
  verdictHelp:
    "A verdict here is about this document alone. Rejecting one marks the entry questionable; it does not dispute the claim itself, and it leaves the other documents exactly as they are.",
  /** Visually hidden, so the pill is announced as what it is. */
  standingQualifier: "What others have put on the record against this entry:",
  /**
   * Said before the server says it.
   *
   * The database refuses a reasonless rejection outright, and a 422 arriving
   * after the field has closed would lose what was typed. Same sentence the
   * entry's own dispute uses, because it is the same rule.
   */
  verdictNeedsReason: "Say why you reject this document.",

  attachOndoId: "+ This owner is on ONDO",
  attachHelp:
    "Enter the ONDO id exactly, e.g. ONDO-1042. There is no name search: ONDO will not tell one account whether another exists, so an id is the only way to name a person here.",
  attachInvalid: "Enter an ONDO id, e.g. ONDO-1042.",
  attachAction: "Link",
  onOndo: "On ONDO — can be asked to verify this entry",
  detach: "Remove ONDO id",

  entriesEmpty: "No provenance entries yet.",
  noDocuments: "No docs",
  verifiedMark: "✓ Verified",
  dateUnknown: "Date unknown",

  /**
   * The two the money field can be wrong in, said in the collector's terms.
   *
   * Neither mentions a constraint or a minor unit: what a person needs to hear
   * is that the figure did not parse, or that a sum needs to say what money it
   * is in. The database is the final word on both.
   */
  insuredAmountInvalid: "Enter the amount insured as a figure, e.g. 1,850,000.",
  insuredCurrencyNeeded: "A sum insured needs its currency — a three-letter code, e.g. GBP.",
  /** The currency box beside the amount. */
  insuredCurrencyLabel: "Currency",
  present: "Present",

  /**
   * What a verification recorded in the wizard means.
   *
   * NOT an apology for a disabled button — the buttons work. Provenance is
   * written by one person and that person is the first to put their name to it.
   * All this says is that the paperwork follows the entry rather than preceding
   * it, and that walking away is still walking away.
   */
  actsPending:
    "Nothing here has been sent. Your verification, your comments and anyone you ask are filed with this entry the moment you submit — and if you leave this form, none of them ever existed.",

  verifyQueued: "You have verified this entry. Press again to take it back.",
  commentQueued: "Filed with the entry at submit. Take one back with the ✕ beside it.",
  requestQueued: "Sent at submit, not before. Take one back with the ✕ beside it.",

  /**
   * Why Dispute is the one action a draft does not offer.
   *
   * Not a rule about who wrote it. A draft is still editable, so disagreeing
   * with it is an edit — and on a RECORDED entry the author can dispute,
   * because the row can no longer be corrected.
   */
  disputeOwnDraft:
    "This entry is still yours to change. Correct it or remove it — a dispute is for a claim that can no longer be edited.",

  /** Why the positions are closed to an account that can only read the entry. */
  readOnlySeat: "This account can read this entry but not put a name to a position on it.",

  /** Why Verify and Dispute close once you hold a position. */
  positionHeld:
    "You already hold a position on this entry. One live position each — take the one you have back first.",

  verifiedByYou: "You verified this entry.",
  disputedByYou: "You disputed this entry.",

  /** Why asking is not open to every reader, now that signing is. */
  askIsYours:
    "Asking is the author’s to do. The request names an account and tells it which work and whose claim, so it is theirs to send.",

  queuedComment: "Comment",
  queuedRequest: "Verification asked of",
  removeComment: "Remove this comment",
  removeRequest: "Remove this request",

  actEmpty: "Say something before recording it.",
  alreadyAsked: "That party is already on the ask list for this entry.",
  askInvalid:
    "Enter an ONDO id, e.g. ONDO-1042, or an email address, e.g. name@example.com.",
  actsFull: `An entry can carry ${String(MAX_ACTS_PER_ENTRY)} of each before it is recorded.`,
  notADraft: "That entry is on the registry. It is written to, not recorded here.",

  /**
   * Attached to the button that does it, and shown only to somebody who can
   * press it.
   *
   * This replaced a general note about editability that every reader of a
   * recorded entry was shown. Both say the same underlying thing — an entry
   * somebody has attested to must still say what they attested to — but a
   * warning on the control is a fact about what is about to happen, where the
   * note was a rule about a form most readers could not use.
   */
  withdrawNote:
    "Withdrawing leaves this entry and every verification on it exactly where they are. It is marked as withdrawn, not deleted.",

  /** The accessible name of the control that opens a document. */
  openDocument: "Open",
} as const;

/**
 * What the record holds, where the record is being read rather than written.
 *
 * The same restraint the evidence grades are written with: counts, never
 * conclusions, and an absence said as an absence. There is no threshold at
 * which this registry starts deciding, and this panel — which replaced four
 * buttons — must not be the place one appears.
 */
export const VERIFICATION_STATE_COPY = {
  title: "Who stands behind this",

  /**
   * Nobody has taken a position. Distinguished from doubt on purpose, the same
   * distinction `confirmationStrip` makes when the counts are zero.
   */
  none:
    "Nobody has put their name to this entry. That is not a doubt about it — it is that nobody has been asked, or nobody has answered.",

  /**
   * Said only when confirmations and disputes both exist.
   *
   * The rule `confirmationStrip` applies to the colour of the strip, in words:
   * a reader shown only the agreements has been told something the record does
   * not say.
   */
  contested:
    "People disagree about this entry. ONDO does not decide between them — both positions are listed above, with who took them and when.",

  /** Above the asks. Never a nudge, and never read as a finding. */
  askedNote: "Nobody is obliged to answer, and nothing is concluded from silence.",
  askedTitle: "Asked to look at this",
  askedAwaiting: "Awaiting an answer",
  askedDeclined: "Declined",
} as const;
